import {
    AfterContentInit,
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ContentChildren,
    DoCheck,
    ElementRef,
    HostBinding,
    HostListener,
    Input,
    OnDestroy,
    OnInit,
    QueryList,
    ViewChild,
    ViewRef,
} from "@angular/core";
import { DropdownKind, SpsIcon, ZStratum } from "@spscommerce/ds-shared";
import { AttributePresenceInput } from "@spscommerce/ng-utils";
import { onNextTick } from "@spscommerce/utils";
import * as nanoidImport from "nanoid";

import { DynamicComponentRef } from "../../classes/dynamic-component-ref";
import { SpsOptionListFactory } from "../../services/sps-option-list.factory";
import { SpsDropdownOptionComponent } from "../dropdown-option/sps-dropdown-option.component";
import { SpsOptionListComponent } from "../option-list/sps-option-list.component";

// Some imports need this to work in all contexts incl ng-packagr/rollup
const nanoid = (<any>nanoidImport).default || nanoidImport;

@Component({
    selector: "sps-dropdown",
    templateUrl: "./sps-dropdown.component.html",
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SpsDropdownComponent implements AfterContentInit, AfterViewInit, DoCheck, OnInit, OnDestroy {
    static readonly displayName = "sps-dropdown";
    static readonly props = {
        spinning: "boolean",
        icon: "SpsIcon",
        kind: "DropdownKind",
        label: "string",
        disabled: "boolean",
        alignLeft: "boolean",
    };

    private static instances = new Set<SpsDropdownComponent>();

    @Input() spinning = false;
    @HostBinding("attr.id") @Input() id: string;
    @Input() tabindex: number;
    @Input() icon: SpsIcon;
    @Input() kind: DropdownKind;
    @Input() label: string;
    @Input() @AttributePresenceInput() disabled: boolean;
    @Input() @AttributePresenceInput() alignLeft: boolean;
    @Input() options: QueryList<SpsDropdownOptionComponent>;

    @HostBinding("class.sps-dropdown") readonly _spsDropdownClass = true;
    @HostBinding("class.sps-dropdown--open") isOpen = false;
    @HostBinding("class.sps-dropdown--opens-upward") opensUpward = false;

    @HostBinding("class.sps-dropdown--no-label")
    get noLabel() {
        return !this.label;
    }

    @ViewChild("button", { static: false }) button: ElementRef;
    optionListRef: DynamicComponentRef<SpsOptionListComponent<any>>;

    @ContentChildren(SpsDropdownOptionComponent) optionsElements: QueryList<SpsDropdownOptionComponent>;

    _buttonId: string;
    _menuId: string;
    private zStratum = ZStratum.BASE;

    private static onKeyDown(event: KeyboardEvent): void {
        for (const instance of SpsDropdownComponent.instances) {
            if (
                !instance.disabled &&
                instance.button.nativeElement.contains(event.target) &&
                instance.optionListRef &&
                !instance.spinning
            ) {
                instance.optionListRef.instance._onKeyDown(event);
            }
        }
    }

    private static onDocumentClick(event: MouseEvent) {
        for (const instance of SpsDropdownComponent.instances) {
            if (!instance.disabled && !instance.spinning) {
                if (instance.button.nativeElement.contains(event.target)) {
                    instance.optionListRef.instance.toggle();
                    instance.optionListRef.changeDetectorRef.detectChanges();
                } else if (!instance.elementRef.nativeElement.contains(event.target)) {
                    instance.optionListRef.instance.close();
                    instance.optionListRef.changeDetectorRef.detectChanges();
                }
                onNextTick(() => {
                    instance.isOpen = instance.optionListRef.instance.isOpen;
                    instance.opensUpward = instance.optionListRef.instance.opensUpward;
                    instance.changeDetectorRef.markForCheck();
                });
            }
        }
    }

    constructor(
        private changeDetectorRef: ChangeDetectorRef,
        private elementRef: ElementRef,
        private optionListFactory: SpsOptionListFactory,
    ) {
        if (SpsDropdownComponent.instances.size === 0) {
            document.addEventListener("keydown", SpsDropdownComponent.onKeyDown);
            document.addEventListener("click", SpsDropdownComponent.onDocumentClick);
        }

        SpsDropdownComponent.instances.add(this);

        this.selectOption = this.selectOption.bind(this);
    }

    ngDoCheck() {
        if (this.optionListRef && !(this.optionListRef as any as ViewRef).destroyed) {
            this.optionListRef.changeDetectorRef.detectChanges();
        }
    }

    ngOnInit() {
        this.id = this.id || nanoid();
        this._buttonId = `${this.id}-button`;
        this._menuId = `${this.id}-menu`;

        if (this.label) {
            this.kind = this.kind || DropdownKind.DEFAULT;
        } else {
            this.kind = DropdownKind.ICON;
            this.icon = this.icon || SpsIcon.ELLIPSES;
        }

        this.optionListRef = this.optionListFactory.create();
        this.optionListRef.updateInputs({
            ariaLabelledBy: this._buttonId,
            id: this._menuId,
            alignLeft: this.alignLeft,
            conformWidth: this.kind !== DropdownKind.ICON,
        });
        this.optionListRef.subscribeToOutput("optionSelected", this.selectOption);
    }

    ngAfterViewInit() {
        if (this.elementRef.nativeElement.matches(".z-stratum-bar sps-dropdown")) {
            this.zStratum = ZStratum.BAR;
        } else if (this.elementRef.nativeElement.matches(".z-stratum-dialog sps-dropdown")) {
            this.zStratum = ZStratum.DIALOG;
        }

        this.optionListRef.updateInputs({
            attachTo: this.button.nativeElement,
            zIndex: this.zStratum + ZStratum.DROPDOWN,
        });
    }

    ngAfterContentInit() {
        this.optionListRef.updateInputs({
            options: (this.options || this.optionsElements).toArray()
        });
    }

    ngOnDestroy() {
        this.optionListRef.destroy();

        if (SpsDropdownComponent.instances.has(this)) {
            SpsDropdownComponent.instances.delete(this);

            if (SpsDropdownComponent.instances.size === 0) {
                document.removeEventListener("keydown", SpsDropdownComponent.onKeyDown);
                document.removeEventListener("click", SpsDropdownComponent.onDocumentClick);
            }
        }
    }

    @HostListener("click", ["$event"])
    _onClick(event: MouseEvent) {
        event.preventDefault();
    }

    @HostListener("focus") onFocus() {
        this.button.nativeElement.focus();
    }

    selectOption(option: SpsDropdownOptionComponent) {
        option.activate();
    }
}
